import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import filterStore from '../stores/filterStore';
import type { EffectiveStatusEntry, EffectiveStatusResponseEntry } from '../api/getEffectiveStatuses';
import { useEffectiveStatuses } from '../api/getEffectiveStatuses';
import type { Option } from '../../../types/shared';
import { RuleEditAutocompleteMultiple } from './RuleEditAutocomplete';
import type { OptionValue } from '../../../types/shared';
import RuleFilterInput from './RuleFilterInput';

interface RuleFilterValueProps {
  groupIndex: number;
  index: number;
}

const RuleFilterValue: FC<RuleFilterValueProps> = observer(({ groupIndex, index }) => {
  const filter = filterStore.getRuleFilter(groupIndex, index);

  const { data: effectiveStatuses } = useEffectiveStatuses({
    config: {
      select: (data) => {
        // @ts-expect-error initial type correction
        const result: Record<keyof EffectiveStatusResponseEntry, Option[]> = {};

        Object.entries(data).map(([k, v]: [k: string, v: EffectiveStatusEntry[]]) => {
          result[k as keyof EffectiveStatusResponseEntry] = v.map((v) => ({ value: v.id, label: v.name }));
        });

        return result;
      },
    },
  });

  const isEffectiveStatus = filter?.field.includes('effective_status');

  const filteredEffectiveStatuses: Option[] = useMemo(() => {
    if (!isEffectiveStatus) return [];

    const key = filter!.field.split('.')[0];

    return effectiveStatuses[key as keyof EffectiveStatusResponseEntry];
  }, [effectiveStatuses, filter, isEffectiveStatus]);

  const handleChangeValue = useCallback(
    (value: OptionValue | OptionValue[]) => {
      filterStore.updateRuleFilterValue(groupIndex, index, value as string[]);
    },
    [groupIndex, index]
  );

  if (isEffectiveStatus) {
    return (
      <RuleEditAutocompleteMultiple
        allSelectable={false}
        value={filter?.value as OptionValue[]}
        InputProps={{
          placeholder: 'Value',
        }}
        onChange={handleChangeValue}
        id="value"
        options={filteredEffectiveStatuses}
      />
    );
  }

  return (
    <RuleFilterInput
      placeholder="Value"
      value={filterStore.getRuleFilter(groupIndex, index)?.value ?? ''}
      onChange={handleChangeValue}
    />
  );
});

export default RuleFilterValue;
